import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useCountdown } from '../hooks/useCountdown';
import { CheckCircle2, ArrowRight, Loader2 } from 'lucide-react';
import { submitToGoogleSheets } from '../utils/googleSheets';
export function CountdownSection() {
  const timeLeft = useCountdown('2026-06-15T00:00:00');
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    org: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formState.name || !formState.email) return;
    setSubmitting(true);
    setError('');
    const result = await submitToGoogleSheets({
      timestamp: new Date().toISOString(),
      name: formState.name,
      email: formState.email,
      org: formState.org
    });
    setSubmitting(false);
    if (result.success) {
      setSubmitted(true);
    } else {
      setError('Something went wrong. Please try again.');
    }
  };
  const timeBlocks = [{
    label: 'Days',
    value: timeLeft.days
  }, {
    label: 'Hours',
    value: timeLeft.hours
  }, {
    label: 'Minutes',
    value: timeLeft.minutes
  }, {
    label: 'Seconds',
    value: timeLeft.seconds
  }];
  return <section id="notify-section" className="py-24 bg-brand-black text-white relative overflow-hidden" data-id="element-62">
      {/* Abstract background shapes */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-10 pointer-events-none" data-id="element-63">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-brand-teal blur-[120px]" data-id="element-64"></div>
        <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-brand-tealLight blur-[120px]" data-id="element-65"></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10" data-id="element-66">
        <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-67">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.8
        }} data-id="element-68">
            <h2 className="font-serif text-4xl md:text-5xl font-bold mb-4" data-id="element-69">
              Launching June 15, 2026
            </h2>
            <p className="text-xl text-gray-300 mb-12 font-light" data-id="element-70">
              Launching with a Live Event. Location to be disclosed. <br data-id="element-71" />
              <span className="text-brand-tealLight font-medium" data-id="element-72">
                Full event details coming soon.
              </span>
            </p>

            <div className="flex gap-4 md:gap-6 mb-16" data-id="element-73">
              {timeBlocks.map((block, idx) => <div key={block.label} className="flex flex-col items-center" data-id="element-74">
                  <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl w-16 h-16 md:w-24 md:h-24 flex items-center justify-center mb-2" data-id="element-75">
                    <span className="text-2xl md:text-4xl font-serif font-bold text-white" data-id="element-76">
                      {block.value.toString().padStart(2, '0')}
                    </span>
                  </div>
                  <span className="text-xs md:text-sm text-gray-400 uppercase tracking-wider" data-id="element-77">
                    {block.label}
                  </span>
                </div>)}
            </div>

            <div className="space-y-4" data-id="element-78">
              <h3 className="text-lg font-semibold text-white mb-4" data-id="element-79">
                Be the first to receive:
              </h3>
              {['Report access', 'Event registration details', 'Exclusive early findings', 'Future opportunities with Acquirely'].map((item, i) => <motion.div key={i} initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.1 + 0.3
            }} className="flex items-center gap-3 text-gray-300" data-id="element-80">
                  <CheckCircle2 className="w-5 h-5 text-brand-tealLight flex-shrink-0" data-id="element-81" />
                  <span data-id="element-82">{item}</span>
                </motion.div>)}
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 md:p-10 shadow-2xl" data-id="element-83">
            {submitted ? <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} className="text-center py-12" data-id="element-84">
                <div className="w-20 h-20 bg-brand-teal/20 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-85">
                  <CheckCircle2 className="w-10 h-10 text-brand-tealLight" data-id="element-86" />
                </div>
                <h3 className="text-2xl font-serif font-bold mb-2" data-id="element-87">
                  You're on the list.
                </h3>
                <p className="text-gray-400" data-id="element-88">
                  We'll notify you as soon as the report is live.
                </p>
              </motion.div> : <>
                <h3 className="text-2xl font-serif font-bold mb-6" data-id="element-89">
                  Get Notified
                </h3>
                <form onSubmit={handleSubmit} className="space-y-5" data-id="element-90">
                  <div data-id="element-91">
                    <label htmlFor="name" className="block text-sm font-medium text-gray-400 mb-1.5" data-id="element-92">
                      Full Name
                    </label>
                    <input type="text" id="name" required value={formState.name} onChange={e => setFormState({
                  ...formState,
                  name: e.target.value
                })} className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-tealLight transition-all" placeholder="Jane Doe" data-id="element-93" />
                  </div>
                  <div data-id="element-94">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-400 mb-1.5" data-id="element-95">
                      Email Address
                    </label>
                    <input type="email" id="email" required value={formState.email} onChange={e => setFormState({
                  ...formState,
                  email: e.target.value
                })} className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-tealLight transition-all" placeholder="jane@company.com" data-id="element-96" />
                  </div>
                  <div data-id="element-97">
                    <label htmlFor="org" className="block text-sm font-medium text-gray-400 mb-1.5" data-id="element-98">
                      Organization (Optional)
                    </label>
                    <input type="text" id="org" value={formState.org} onChange={e => setFormState({
                  ...formState,
                  org: e.target.value
                })} className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-tealLight transition-all" placeholder="Company Name" data-id="element-99" />
                  </div>

                  {error && <p className="text-red-400 text-sm mb-2" data-id="element-100">{error}</p>}

                  <div className="pt-4" data-id="element-101">
                    <motion.button whileHover={{
                  scale: submitting ? 1 : 1.02
                }} whileTap={{
                  scale: submitting ? 1 : 0.98
                }} type="submit" disabled={submitting} className="relative w-full group bg-brand-teal text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed" data-id="element-102">
                      {/* Pulse effect */}
                      {!submitting && <span className="absolute inset-0 rounded-lg bg-brand-tealLight opacity-50 group-hover:animate-ping" data-id="element-103"></span>}
                      <span className="relative z-10 flex items-center gap-2" data-id="element-104">
                        {submitting ? <>
                            <Loader2 className="w-5 h-5 animate-spin" data-id="element-105" />
                            Submitting...
                          </> : <>
                            Notify Me{' '}
                            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-106" />
                          </>}
                      </span>
                    </motion.button>
                  </div>
                </form>
              </>}
          </motion.div>
        </div>
      </div>
    </section>;
}